// src/pages/PINChange.js
import React, { useState } from "react";
import { verifyPin, setPin } from "../utils/PINService";
import { setPinConfirmedNow, clearPinConfirmed } from "../utils/PINSession";
import { useHistory } from "react-router-dom";
import FormLayout from "../layouts/FormLayout";
import { isZh } from "../utils/language";

const PINChange = () => {
  const [oldPin, setOldPin] = useState("");
  const [pin, setPinNew] = useState("");
  const [pin2, setPin2] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const history = useHistory();

  async function submitHandler(e) {
    e.preventDefault();
    if (!oldPin) {
      setMessage({ type: "error", text: isZh() ? "請輸入舊 PIN。" : "Masukkan PIN lama." });
      return;
    }
    if (!/^\d{6}$/.test(oldPin)) {
      setMessage({ type: "error", text: isZh() ? "舊 PIN 必須為 6 位數字。" : "PIN lama harus 6 digit angka." });
      return;
    }
    if (!/^\d{6}$/.test(pin)) {
      setMessage({ type: "error", text: isZh() ? "新 PIN 必須為 6 位數字。" : "PIN baru harus 6 digit angka." });
      return;
    }
    if (pin !== pin2) {
      setMessage({ type: "error", text: isZh() ? "新 PIN 必須為 6 位數字。" : "PIN baru harus 6 digit angka." });
      return;
    }

    setLoading(true);
    try {
      const v = await verifyPin(oldPin);
      const ok = !!(v && (v.ok === true || v.message?.toLowerCase()?.includes("valid")));
      if (!ok) {
        setMessage({ type: "error", text: isZh() ? "舊 PIN 錯誤。" : "PIN lama salah." });
        setLoading(false);
        return;
      }

      const s = await setPin(pin);
      const setOk = !!(s && (s.ok === true || s.message?.toLowerCase()?.includes("disimpan") || s.message?.toLowerCase()?.includes("perbarui")));
      if (setOk) {
        clearPinConfirmed();
        setMessage({
          type: "success",
          text: isZh()
            ? "PIN 已成功更改。請重新輸入 PIN 以繼續。"
            : "PIN berhasil diubah. Silakan masukkan ulang PIN untuk melanjutkan."
        });
        //setMessage({ type: "success", text: isZh() ? "PIN 已成功更改。" : "PIN berhasil diubah." });
        setTimeout(() => {
          history.replace("/"); 
        }, 1200);
      } else {
        const msg = (s && s.message) ||  (isZh() ? "更改 PIN 失敗。" : "Gagal mengganti PIN.");
        setMessage({ type: "error", text: msg });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("changePin error", err);
      setMessage({ type: "error", text: isZh() ? "更改 PIN 失敗，請再試。" : "Gagal mengganti PIN. Coba lagi." });
    } finally {
      setLoading(false);
      setOldPin("");
      setPinNew("");
      setPin2("");
    }
  }

  // LEFT panel (desktop) - full parent summary/details (example)
  const leftPanelNode = (
    <div></div>
  )

  // LEFT mobile summary (single-line) shown when collapsed on mobile
  const leftSummaryNode = (
    <div style={{ fontWeight: 600 }}>&nbsp;</div>
  );

  // RIGHT panel (desktop) - instructions / petunjuk pengisian
  const rightPanelNode = (
    <div>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>
        {isZh() ? "填寫說明" : "Petunjuk Pengisian"}
      </div>
      <ol>
        <li>{isZh() ? "PIN 用於存取受保護頁面。" : "PIN digunakan untuk akses halaman terbatas."}</li>
        <li>{isZh() ? "必須為 6 位數字。" : "Wajib menggunakan 6 digit angka."}</li>
        <li>{isZh() ? "請勿使用容易猜測的 PIN。" : "Jangan gunakan PIN yang mudah ditebak."}</li>
        <li>{isZh() ? "請妥善記住您的 PIN。" : "Ingat PIN Anda baik-baik."}</li>
      </ol>
    </div>
  );

  const infoNode = message ? (
    <div style={{ padding: 8, borderRadius: 6, background: message.type === "error" ? "#ffecec" : "#e9f6ee", color: message.type === "error" ? "#9b1c1c" : "#064e2d" }}>
      {message.text}
    </div>
  ) : null;

  return (
    <FormLayout
      title={isZh() ? "更改 PIN" : "Ubah PIN"}
      onSubmit={submitHandler}
      submitLabel={loading ? (isZh() ? "處理中..." : "Memproses...") : (isZh() ? "更改 PIN" : "Ganti PIN")}      
      hideTopButton={false}
      leftPanelNode={leftPanelNode}
      leftSummaryNode={leftSummaryNode}
      rightPanelNode={rightPanelNode}
      rightSummaryTitle="Petunjuk"
      infoNode={infoNode}
    >
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 6, color: "#2b4a84", fontWeight: 600 }}>
          {isZh() ? "舊 PIN：" : "PIN Lama:"}
        </label>
        <input
          value={oldPin}
          onChange={(e) => setOldPin(e.target.value.replace(/\D/g,"").slice(0,6))}
          type="password"
          inputMode="numeric"
          pattern="\d{6}"
          maxLength={6}
          placeholder={ isZh() ? "請輸入舊 PIN（6 位數字）" : "Masukkan PIN lama (6 digit)" }
          style={{ width: "100%", padding: 10, border: "2px solid #2b4a84", borderRadius: 6 }}
          aria-label="PIN lama"
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 6, color: "#2b4a84", fontWeight: 600 }}>
          {isZh() ? "新 PIN：" : "PIN Baru:"}
        </label>
        <input
          value={pin}
          onChange={(e) => setPinNew(e.target.value.replace(/\D/g,"").slice(0,6))}
          type="password"
          inputMode="numeric"
          pattern="\d{6}"
          maxLength={6}
          placeholder={isZh() ? "請輸入新 PIN（6 位數字）" : "Masukkan PIN baru (6 digit)"}
          style={{ width: "100%", padding: 10, border: "2px solid #2b4a84", borderRadius: 6 }}
          aria-label="PIN baru"
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: "block", marginBottom: 6, color: "#2b4a84", fontWeight: 600 }}>
          {isZh() ? "確認新 PIN：" : "Konfirmasi PIN Baru:"}
        </label>
        <input
          value={pin2}
          onChange={(e) => setPin2(e.target.value.replace(/\D/g,"").slice(0,6))}
          type="password"
          inputMode="numeric"
          pattern="\d{6}"
          maxLength={6}
          placeholder={isZh() ? "請再次輸入新 PIN" : "Ulangi PIN baru"}
          style={{ width: "100%", padding: 10, border: "2px solid #2b4a84", borderRadius: 6 }}
          aria-label="Konfirmasi PIN baru"
        />
      </div>

      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem", display:"none" }}>
        <button type="submit" disabled={loading} className="btn-submit" style={{ padding: "8px 12px", borderRadius: 8 }}>
          {loading ? (isZh() ? "處理中..." : "Memproses...") : (isZh() ? "更改 PIN" : "Ganti PIN")}
        </button>

        <button type="button" onClick={() => history.replace("/")} style={{ fontSize: "1rem", padding: "8px 12px", borderRadius: 8, background: "#fff", border: "1px solid #e6eaf1" }}>
          {isZh() ? "取消" : "Batal"}
        </button>
      </div>

      <div style={{ height: 8 }} />
    </FormLayout>
  );
};

export default PINChange;
